import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast as sonnerToast } from 'sonner';
import { STOP_OFFERING_WAITING_FOR_ORDERS_MARKER } from '@threadheads/shared';
import { trpc, type RouterInputs, type RouterOutputs } from '@/lib/trpc';

/**
 * BLU-711 Part B Stage 1 — typed seams for the shared-SKU endpoints.
 *
 * This file is the ONLY place the frontend names the shared-SKU procedures,
 * so any backend rename is a one-file reconciliation. Contract (verified
 * against apps/backend/src/routers/inventory.ts + inventory.service.ts):
 *
 *   inventory.setSkuShared                  { skuId, isShared }             → { updatedCount }
 *   inventory.bulkSetSkuShared              { skuIds, isShared }            → { updatedCount }
 *   inventory.getSharedSkuMappingCounts     { skuIds }                      → { counts: Record<skuId, number> }
 *   inventory.getClientSharedSkuMappingCount{ clientOrganizationId }        → { count }
 *
 * Phase 92 (92-30, consumed by 92-31 and 92-31b): the 3PL's Replace and Stop
 * offering. `previewReplaceBlank`, `getSupplyBlankUsage` and
 * `previewStopOfferingBlanks` are READ-ONLY tRPC mutations (so up to 200 pairs
 * or 500 ids travel in the POST body, never a URL over 2083 characters), so
 * their hooks below hold the last result themselves (`useKeptMutationRead`).
 * Refusals come back as data (`{ ok: false, refusal }`): tRPC 11.7.1 drops
 * error causes on the wire.
 *
 * Un-sharing a SKU with live client mappings is refused server-side with a
 * `conflict` (tRPC CONFLICT) whose message names the live-mapping count; the
 * DB reverse guard (SQLSTATE P0711) is the TOCTOU backstop behind it.
 */

/** Owner-side nomination of a single SKU as a shared fallback pool. */
export function useSetSkuShared() {
  return trpc.inventory.setSkuShared.useMutation();
}

/**
 * Live substitution-mapping count for one SKU — feeds the EditSkuModal
 * confirm-on-disable copy. Fetched lazily (only while the confirm is open) so
 * opening the modal costs nothing.
 */
export function useSkuSharedMappingCount(skuId: string | null, enabled: boolean) {
  const query = trpc.inventory.getSharedSkuMappingCounts.useQuery(
    { skuIds: skuId ? [skuId] : [] },
    { enabled: enabled && Boolean(skuId), staleTime: 15_000 }
  );
  return {
    ...query,
    mappingCount: skuId ? query.data?.counts?.[skuId] : undefined,
  };
}

/**
 * Junction-card figure: live mappings between the caller (as location owner)
 * and one client org. Owner-scoped server-side — a non-owner simply reads 0.
 */
export function useClientSharedSkuMappingCount(
  clientOrganizationId: string | null,
  enabled = true
) {
  return trpc.inventory.getClientSharedSkuMappingCount.useQuery(
    { clientOrganizationId: clientOrganizationId ?? '' },
    { enabled: enabled && Boolean(clientOrganizationId), staleTime: 60_000 }
  );
}

/**
 * True when the server refused to un-share because a client substitution
 * mapping is still live. The service raises a structured `conflict` (mapped to
 * tRPC CONFLICT by toTRPCError) and the DB reverse guard raises SQLSTATE
 * P0711 behind it — match either so guidance renders even if the error shape
 * flattens across the wire.
 */
export function isMappingStillLiveError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const err = error as { message?: unknown; data?: { code?: unknown } | null };
  if (err.data && typeof err.data === 'object' && (err.data as { code?: unknown }).code === 'CONFLICT') {
    return true;
  }
  return (
    typeof err.message === 'string' &&
    (err.message.includes('P0711') || err.message.includes('Cannot un-share'))
  );
}

/**
 * Phase 92 (92-30 review M2, UI in 92-31b): true when Stop offering was
 * refused only because client orders already placed still hold the blank's
 * supply mappings. tRPC drops error causes (BLU-1277), so this reads the
 * message marker, never a cause or meta code. The same message also says
 * "Cannot un-share", so check this BEFORE `isMappingStillLiveError`.
 */
export function isWaitingForOrdersError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const message = (error as { message?: unknown }).message;
  return typeof message === 'string' && message.startsWith(`${STOP_OFFERING_WAITING_FOR_ORDERS_MARKER}:`);
}

// ---------------------------------------------------------------------------
// Phase 92 (92-31): Replace with, and Stop offering
// ---------------------------------------------------------------------------

export type ReplaceBlankPair = { fromSkuId: string; toSkuId: string };
export type ReplacePreview = RouterOutputs['inventory']['previewReplaceBlank'];
export type ReplacePreviewOk = Extract<ReplacePreview, { ok: true }>;
export type ReplacePreviewRow = ReplacePreviewOk['rows'][number];
export type ReplaceApplyResult = RouterOutputs['inventory']['replaceBlank'];
export type SupplyBlankUsageResult = RouterOutputs['inventory']['getSupplyBlankUsage'];
export type StopOfferingReview = RouterOutputs['inventory']['previewStopOfferingBlanks'];
export type OwnSharedGarmentBlanks = RouterOutputs['inventory']['getOwnSharedGarmentBlanks'];

/** What a read-only mutation hook hands its screen (review P4). */
export interface KeptRead<T> {
  /** The last answer; kept on screen while the next call runs. */
  data: T | undefined;
  /** A call is in flight (the first one, or a newer one over kept data). */
  loading: boolean;
  /** The last call failed with no answer; cleared by the next answer. */
  error: unknown;
  /** Send the same input again. */
  retry: () => void;
}

/**
 * Sends a read-only mutation whenever its input changes (after `debounceMs`
 * when set), keeps the last answer while the next one runs, and drops any
 * answer that is not the newest call's (out of order). `input === null` sends
 * nothing and clears the screen, so non-admins and blanks nobody shares never
 * fire a call.
 */
function useKeptMutationRead<TInput, TOutput>(
  mutateAsync: (input: TInput) => Promise<TOutput>,
  input: TInput | null,
  debounceMs = 0,
): KeptRead<TOutput> {
  const key = input === null ? null : JSON.stringify(input);
  const [data, setData] = useState<TOutput | undefined>(undefined);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(false);
  const [nonce, setNonce] = useState(0);
  const latest = useRef(0);
  const mutateRef = useRef(mutateAsync);
  mutateRef.current = mutateAsync;
  const inputRef = useRef(input);
  inputRef.current = input;

  useEffect(() => {
    const id = ++latest.current;
    if (key === null) {
      setData(undefined);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    const send = () => {
      mutateRef.current(inputRef.current as TInput).then(
        (result) => {
          if (id !== latest.current) return;
          setData(result);
          setError(null);
          setLoading(false);
        },
        (err: unknown) => {
          if (id !== latest.current) return;
          setError(err ?? new Error('failed'));
          setLoading(false);
        },
      );
    };
    if (debounceMs <= 0) {
      send();
      return;
    }
    const timer = setTimeout(send, debounceMs);
    return () => clearTimeout(timer);
  }, [key, nonce, debounceMs]);

  // An answer that lands after unmount is dropped.
  useEffect(
    () => () => {
      latest.current += 1;
    },
    [],
  );

  const retry = useCallback(() => setNonce((n) => n + 1), []);
  return { data, loading, error, retry };
}

/**
 * "Used by {count} client products" for blanks of the caller's own (UI-SPEC
 * 3.2). One `getSupplyBlankUsage` call when the ids change; `null` sends
 * nothing. Pass ids only for an admin looking at a blank it shares.
 */
export function useSupplyBlankUsage(skuIds: string[] | null): KeptRead<SupplyBlankUsageResult> {
  const mutation = trpc.inventory.getSupplyBlankUsage.useMutation();
  return useKeptMutationRead(mutation.mutateAsync, skuIds && skuIds.length > 0 ? { skuIds } : null);
}

/**
 * The Replace review (UI-SPEC 3.4): one `previewReplaceBlank` dry run per
 * change of the chosen replacement, 300 ms after the last change. `null`
 * (nothing chosen) sends nothing.
 */
export function usePreviewReplaceBlank(pairs: ReplaceBlankPair[] | null): KeptRead<ReplacePreview> {
  const mutation = trpc.inventory.previewReplaceBlank.useMutation();
  return useKeptMutationRead(
    mutation.mutateAsync,
    pairs && pairs.length > 0 ? { replacements: pairs } : null,
    300,
  );
}

/**
 * The bulk Stop offering review (UI-SPEC 3.3, 92-31b): which chosen blanks
 * client products still use. One `previewStopOfferingBlanks` call per id set.
 */
export function usePreviewStopOfferingBlanks(skuIds: string[] | null): KeptRead<StopOfferingReview> {
  const mutation = trpc.inventory.previewStopOfferingBlanks.useMutation();
  return useKeptMutationRead(mutation.mutateAsync, skuIds && skuIds.length > 0 ? { skuIds } : null);
}

/**
 * One of the caller's own shared garments' blanks with size and colour, for
 * the bulk Replace value match (92-31b). A query: its input is one key.
 */
export function useOwnSharedGarmentBlanks(garmentKey: string | null) {
  return trpc.inventory.getOwnSharedGarmentBlanks.useQuery(
    { garmentKey: garmentKey ?? '' },
    { enabled: Boolean(garmentKey), staleTime: 30_000 },
  );
}

/**
 * Bulk "Stop offering blanks" (92-31b): un-shares the chosen blanks and
 * leaves any that client products still use shared (`skipInUse`); the result
 * then carries `skippedInUseCount`.
 */
export function useStopOfferingBlanks() {
  const mutation = trpc.inventory.bulkSetSkuShared.useMutation();
  const mutateAsync = mutation.mutateAsync;
  const stopOffering = useCallback(
    (skuIds: string[]) => {
      const input: RouterInputs['inventory']['bulkSetSkuShared'] = { skuIds, isShared: false, skipInUse: true };
      return mutateAsync(input);
    },
    [mutateAsync],
  );
  return { stopOffering, isPending: mutation.isPending };
}

/**
 * The confirm "Replace for new orders" (and `Cmd/Ctrl+Enter`) is live only
 * when the newest review has loaded with no error and at least one row's
 * print SKU changes (UI-SPEC 3.4).
 */
export function reviewAllowsReplace(review: Pick<KeptRead<ReplacePreview>, 'data' | 'loading' | 'error'>): boolean {
  return !review.loading && !review.error && review.data?.ok === true && review.data.changes > 0;
}

/** How one Replace click ended, after the hook's own toast (review P7). */
export type ReplaceBlankOutcome =
  /** Rows moved; the success toast has been shown. */
  | { kind: 'replaced'; count: number }
  /** A retry of a replace that had already committed; the "already done" toast has been shown. */
  | { kind: 'already_applied' }
  /** The change is over the server's row cap: show the inline line and keep the confirm off. */
  | { kind: 'too_many_rows' }
  /** The server answered no, or moved nothing; the "nothing was changed" toast has been shown. */
  | { kind: 'refused' }
  /** No answer (timeout, network, 5xx): never say nothing changed; re-run the review. */
  | { kind: 'maybe_finishing' };

/**
 * True when a failed call has no usable server answer: no tRPC error shape
 * (network, a proxy's HTML 502, a timeout) or a 5xx. The write may have
 * committed, so the screen must never say nothing changed.
 */
export function isNoAnswerError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return true;
  const data = (error as { data?: { code?: unknown; httpStatus?: unknown } | null }).data;
  if (!data || typeof data !== 'object' || typeof data.code !== 'string') return true;
  if (typeof data.httpStatus === 'number' && data.httpStatus >= 500) return true;
  return data.code === 'INTERNAL_SERVER_ERROR' || data.code === 'TIMEOUT' || data.code === 'CLIENT_CLOSED_REQUEST';
}

/**
 * Applies a Replace for new orders (92-29's one-transaction RPC through
 * 92-30) and maps every ending to one outcome, once per click: a mutation
 * that settles twice for one click (a transport retry) toasts once and
 * reports once (UI 26). The toasts live here so 92-31b's dialog shows the
 * same lines.
 */
export function useReplaceBlank() {
  const { t } = useTranslation('inventory');
  const mutation = trpc.inventory.replaceBlank.useMutation();
  const mutate = mutation.mutate;
  const attempt = useRef(0);
  const settled = useRef(new Set<number>());
  const inFlight = useRef(false);
  const [pending, setPending] = useState(false);

  const replace = useCallback(
    (pairs: ReplaceBlankPair[], blankLabel: string, onOutcome: (outcome: ReplaceBlankOutcome) => void) => {
      if (inFlight.current || pairs.length === 0) return;
      inFlight.current = true;
      setPending(true);
      const id = ++attempt.current;
      const settle = (outcome: ReplaceBlankOutcome) => {
        if (settled.current.has(id)) return;
        settled.current.add(id);
        inFlight.current = false;
        setPending(false);
        if (outcome.kind === 'replaced') {
          sonnerToast.success(t('sharedSkus.replace.success', { count: outcome.count, blank: blankLabel }));
        } else if (outcome.kind === 'already_applied') {
          sonnerToast.success(t('sharedSkus.replace.alreadyApplied', { blank: blankLabel }));
        } else if (outcome.kind === 'refused') {
          sonnerToast.error(t('sharedSkus.replace.failed'), { duration: Infinity });
        }
        onOutcome(outcome);
      };
      mutate(
        { replacements: pairs },
        {
          onSuccess: (result: ReplaceApplyResult) => {
            if (!result.ok) {
              settle(result.refusal === 'too_many_rows' ? { kind: 'too_many_rows' } : { kind: 'refused' });
            } else if (result.alreadyApplied) {
              settle({ kind: 'already_applied' });
            } else if (result.replaced === 0) {
              // Every row was refused under the write's locks (it came into
              // use, clashed or lost consent after the review): nothing moved.
              settle({ kind: 'refused' });
            } else {
              settle({ kind: 'replaced', count: result.replaced });
            }
          },
          onError: (error: unknown) => {
            settle(isNoAnswerError(error) ? { kind: 'maybe_finishing' } : { kind: 'refused' });
          },
        },
      );
    },
    [mutate, t],
  );

  return { replace, pending };
}
